import React from 'react';
import { ChevronLeft, Languages, Menu, Sun, Moon } from 'lucide-react';
import { useTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

interface TopBarProps {
  title: string;
  description?: string;
  onOpenMobileSidebar: () => void;
  primaryAction?: { label: string; onClick: () => void; tone?: 'primary' | 'upgrade'; icon?: React.ReactNode };
}

export const TopBar: React.FC<TopBarProps> = ({
  title,
  description,
  onOpenMobileSidebar,
  primaryAction,
}) => {
  const { t, tx } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="app-topbar sticky top-0 z-30">
      <div className="app-topbar-inner">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onOpenMobileSidebar}
            className="topbar-mobile-menu icon-button"
            aria-label={t('topbar.openMenu')}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="topbar-page-copy min-w-0">
            <div className="topbar-breadcrumb hidden sm:flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
              <span>{tx('مساحة العمل')}</span>
              <ChevronLeft className="w-3 h-3 rtl-mirror" />
              <span>{title}</span>
            </div>
            <div className="topbar-title-line"><h1>{title}</h1>{description && <p>{description}</p>}</div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            onClick={toggleTheme}
            className="hidden sm:flex icon-button"
            title={tx('تبديل المظهر')}
            aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button onClick={toggleLanguage} className="icon-button" title={t('common.language')} aria-label={t('common.language')}>
            <Languages className="w-[18px] h-[18px]" />
            <span className="sr-only">{t('common.language')}</span>
            <span className="hidden sm:inline text-[10px] font-bold uppercase">{language === 'ar' ? 'EN' : 'AR'}</span>
          </button>
          {primaryAction && (
            <button aria-label={primaryAction.label} onClick={primaryAction.onClick} className={`topbar-primary-action btn ${primaryAction.tone === 'upgrade' ? 'btn-upgrade' : 'btn-primary'}`}>
              {primaryAction.icon}
              <span>{primaryAction.label}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
